import { useLocation, useMatch, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useConnections } from '@/auth/useSession';
import { toast } from '@/components/ui/toast';
import { useSampleActive } from '@/lib/sample';
import { isActivePath } from './nav';
import { SCREENS } from './screens';

/** Screens that state the no-connections case themselves; never navigated away from (parity §0). */
const REDIRECT_EXEMPT = [SCREENS.tiktokAdsConnected, SCREENS.performance, SCREENS.overview, SCREENS.noConnections, SCREENS.revenueForecasts, SCREENS.billing, SCREENS.competitorInsights].map((s) => s.path);
/** Screens where the "not connected" notification would repeat what the page says, or does not apply. */
const NOTICE_SUPPRESSED = [SCREENS.tiktokAdsConnected, SCREENS.noConnections, SCREENS.performance, SCREENS.overview, SCREENS.revenueForecasts, SCREENS.apiAccess, SCREENS.billing, SCREENS.competitorInsights].map((s) => s.path);

const NOTICE_ID = 'no-connections';
const matchesAny = (pathname: string, paths: string[]) => paths.some((p) => isActivePath(pathname, p));

/**
 * An account with no ad-network connection: once per app start, move it to
 * /no-connections (unless the screen explains the state itself), and keep one
 * dismissible notification with a link until a connection exists. Not while
 * the tab explores sample data: the sample banner says it, with its own link.
 */
export function useConnectionsGate(): void {
  const connections = useConnections();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onNotFound = useMatch({ from: '/_shell/$', shouldThrow: false }) !== undefined;
  const redirected = useRef(false);
  const dismissed = useRef(false);
  const sample = useSampleActive();
  const none = !sample && connections.isSuccess && connections.data.length === 0;

  useEffect(() => {
    if (!none || redirected.current) return;
    redirected.current = true;
    if (!matchesAny(pathname, REDIRECT_EXEMPT)) void navigate({ to: SCREENS.noConnections.path, replace: true });
  }, [none, pathname, navigate]);

  useEffect(() => {
    if (!none || dismissed.current || onNotFound || matchesAny(pathname, NOTICE_SUPPRESSED)) {
      toast.dismiss(NOTICE_ID);
      return;
    }
    toast('Lemon AI is not connected to any ad networks yet', {
      id: NOTICE_ID,
      duration: Infinity,
      description: 'Connect your ad networks to see your creatives’ performance.',
      action: {
        label: 'Connect',
        onClick: () => {
          dismissed.current = true;
          void navigate({ to: SCREENS.noConnections.path });
        },
      },
      cancel: { label: 'Dismiss', onClick: () => { dismissed.current = true; } },
    });
  }, [none, onNotFound, pathname, navigate]);
}
